/**
 * SIM-MGMT — SimModelPreview
 *
 * A cheap, NON-graphical structural thumbnail of a saved simulation's model.
 * Parses the persisted `nodes`/`edges` JSON blobs (the SAME blobs loadCloudSim
 * parses) and renders a compact component-by-type summary, e.g.
 *
 *     12 nodes · 14 edges · 3 pumps · 2 heat exchangers · 1 turbine
 *
 * Why structural (not a mini react-flow canvas): standing up a react-flow
 * instance per card is expensive (layout, measurement, viewport) and would
 * require deserialising every saved sim eagerly. A type histogram is O(nodes),
 * runs no solver, and gives the user enough to disambiguate models at a glance.
 *
 * The blobs are JSON-stringified arrays of react-flow nodes/edges; each node
 * carries a `type` discriminator (matches `nodeTypes` in editor/CustomNodes).
 * All parsing is guarded — a corrupt/missing blob renders a neutral fallback,
 * never throws.
 */
import React from 'react';

/** Friendly singular/plural labels for the component types that benefit from
 *  a human-readable summary. Types not listed fall back to a humanised key. */
const TYPE_LABELS: Record<string, { one: string; many: string }> = {
  pump: { one: 'pump', many: 'pumps' },
  turbine: { one: 'turbine', many: 'turbines' },
  compressor: { one: 'compressor', many: 'compressors' },
  heatExchanger: { one: 'HX', many: 'HX' },
  reactor: { one: 'reactor', many: 'reactors' },
  dataCenter: { one: 'data center', many: 'data centers' },
  coolingTower: { one: 'cooling tower', many: 'cooling towers' },
  expansionValve: { one: 'valve', many: 'valves' },
  ejector: { one: 'ejector', many: 'ejectors' },
  absorber: { one: 'absorber', many: 'absorbers' },
  generator: { one: 'generator', many: 'generators' },
  thermalStorage: { one: 'thermal store', many: 'thermal stores' },
  pipe: { one: 'pipe', many: 'pipes' },
  junction: { one: 'junction', many: 'junctions' },
  boundaryCondition: { one: 'boundary', many: 'boundaries' },
  mixer: { one: 'mixer', many: 'mixers' },
  splitter: { one: 'splitter', many: 'splitters' },
  hrsg: { one: 'HRSG', many: 'HRSG' },
  gasTurbine: { one: 'gas turbine', many: 'gas turbines' },
  combustor: { one: 'combustor', many: 'combustors' },
  gasTurbineUnit: { one: 'GT unit', many: 'GT units' },
  boiler: { one: 'boiler', many: 'boilers' },
  condenser: { one: 'condenser', many: 'condensers' },
  saturator: { one: 'saturator', many: 'saturators' },
  separator: { one: 'separator', many: 'separators' },
  reheater: { one: 'reheater', many: 'reheaters' },
  openFeedwaterHeater: { one: 'OFWH', many: 'OFWH' },
  steamJetCooler: { one: 'steam-jet cooler', many: 'steam-jet coolers' },
  airCooledCondenser: { one: 'ACC', many: 'ACC' },
  adiabaticPrecooler: { one: 'precooler', many: 'precoolers' },
  indirectEvaporativeCooler: { one: 'IEC', many: 'IEC' },
  directLiquidCooling: { one: 'DLC', many: 'DLC' },
  ldesThermalBattery: { one: 'LDES battery', many: 'LDES batteries' },
  absorptionChiller: { one: 'abs. chiller', many: 'abs. chillers' },
};

/** Pipes/junctions/boundaries are plumbing, not "equipment" — collapse them out
 *  of the headline equipment chips so the summary highlights real components. */
const PLUMBING_TYPES = new Set(['pipe', 'junction', 'boundaryCondition']);

function humanise(type: string, count: number): string {
  const label = TYPE_LABELS[type];
  if (label) return count === 1 ? label.one : label.many;
  // Fallback: split camelCase into words, lowercase.
  const words = type.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return count === 1 ? words : `${words}s`;
}

interface Counts {
  nodeCount: number;
  edgeCount: number;
  /** equipment type -> count, plumbing collapsed out, sorted desc by count. */
  byType: Array<{ type: string; count: number }>;
}

/** Parse the persisted JSON blobs into a structural histogram. Guarded: a
 *  missing/corrupt blob yields zeroed counts rather than throwing. */
export function summariseModel(
  nodesJson?: string | null,
  edgesJson?: string | null,
): Counts {
  let nodes: Array<{ type?: string }> = [];
  let edges: unknown[] = [];
  try {
    if (nodesJson) {
      const parsed = JSON.parse(nodesJson);
      if (Array.isArray(parsed)) nodes = parsed;
    }
  } catch {
    /* corrupt blob — leave empty */
  }
  try {
    if (edgesJson) {
      const parsed = JSON.parse(edgesJson);
      if (Array.isArray(parsed)) edges = parsed;
    }
  } catch {
    /* corrupt blob — leave empty */
  }

  const tally = new Map<string, number>();
  for (const n of nodes) {
    const t = typeof n?.type === 'string' && n.type ? n.type : 'custom';
    if (PLUMBING_TYPES.has(t)) continue;
    tally.set(t, (tally.get(t) ?? 0) + 1);
  }
  const byType = Array.from(tally.entries())
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count || a.type.localeCompare(b.type));

  return { nodeCount: nodes.length, edgeCount: edges.length, byType };
}

export interface SimModelPreviewProps {
  nodesJson?: string | null;
  edgesJson?: string | null;
  /** Cap on how many equipment-type chips to show before "+N more". */
  maxTypes?: number;
  className?: string;
}

/**
 * Compact, single-line structural preview. Renders nothing graphical — just a
 * node/edge count plus the top equipment types. Safe to render many per page.
 */
export function SimModelPreview({
  nodesJson,
  edgesJson,
  maxTypes = 4,
  className,
}: SimModelPreviewProps) {
  const { nodeCount, edgeCount, byType } = React.useMemo(
    () => summariseModel(nodesJson, edgesJson),
    [nodesJson, edgesJson],
  );

  if (nodeCount === 0) {
    return (
      <div
        className={`type-meta text-text-muted italic ${className ?? ''}`}
        data-testid="sim-model-preview-empty"
      >
        Empty model
      </div>
    );
  }

  const shown = byType.slice(0, maxTypes);
  const hiddenCount = byType.length - shown.length;

  return (
    <div
      className={`type-meta text-text-secondary flex flex-wrap items-center gap-x-1.5 gap-y-0.5 ${className ?? ''}`}
      data-testid="sim-model-preview"
    >
      <span className="font-bold text-text-primary">
        {nodeCount} {nodeCount === 1 ? 'node' : 'nodes'}
      </span>
      <span aria-hidden>·</span>
      <span>
        {edgeCount} {edgeCount === 1 ? 'edge' : 'edges'}
      </span>
      {shown.map(({ type, count }) => (
        <React.Fragment key={type}>
          <span aria-hidden>·</span>
          <span>
            {count} {humanise(type, count)}
          </span>
        </React.Fragment>
      ))}
      {hiddenCount > 0 && (
        <>
          <span aria-hidden>·</span>
          <span className="text-text-muted">+{hiddenCount} more</span>
        </>
      )}
    </div>
  );
}
